import { Group, Text, Button, Box, Burger, Drawer, Stack, ScrollArea } from '@mantine/core';
import { useDisclosure, useWindowScroll } from '@mantine/hooks';
import { IconRobot } from '@tabler/icons-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { NAV_LINKS } from '../../data/navigation';

/**
 * Navbar component providing navigation links and access to the bot management tool.
 * Handles both page routing and smooth scrolling for hash links.
 * @param {Object} props - Component properties.
 * @param {Function} props.onOpen - Function to open the bot management modal.
 */
export default function Navbar({ onOpen }) {
    const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
    const [scroll] = useWindowScroll();

    const navigate = useNavigate();
    const location = useLocation();

    const isScrolled = scroll.y > 20;

    /**
     * Handles navigation for both internal routes and hash-based section links.
     * @param {string} link - The destination URL or hash link.
     */
    const handleNavigation = (link) => {
        closeDrawer();

        if (link.startsWith('/')) {
            navigate(link);
            return;
        }

        if (link.startsWith('#')) {
            if (location.pathname === '/') {
                const element = document.getElementById(link.replace('#', ''));
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            } else {
                navigate('/' + link);
            }
        }
    };

    /**
     * Handles the "Launch" button click, either opening the modal or navigating home.
     */
    const handleLaunch = () => {
        closeDrawer();
        if (location.pathname === '/' && onOpen) {
            onOpen();
        } else {
            navigate('/');
        }
    };

    return (
        <Box
            style={{
                position: 'fixed',
                top: 0, left: 0, right: 0,
                height: 70,
                zIndex: 1000,
                transition: 'all 0.3s ease',
                backgroundColor: isScrolled ? 'rgba(10, 10, 10, 0.85)' : 'transparent',
                backdropFilter: isScrolled ? 'blur(12px)' : 'none',
                borderBottom: isScrolled ? '1px solid rgba(255,255,255,0.08)' : '1px solid transparent',
            }}
        >
            <Box px="md" h="100%" maw={1200} mx="auto">
                <Group justify="space-between" align="center" h="100%">

                    {/* LOGO: Use Link to prevent reload */}
                    <Link to="/" style={{ textDecoration: 'none' }}>
                        <Group gap={10} style={{ cursor: 'pointer' }}>
                            <IconRobot size={30} color="#228be6" style={{ filter: 'drop-shadow(0 0 10px rgba(34, 139, 230, 0.4))' }} />
                            <Text fw={900} size="xl" c="white" style={{ letterSpacing: '-0.5px' }}>
                                Kahoot <span style={{ color: '#228be6' }}>Bot</span>
                            </Text>
                        </Group>
                    </Link>

                    {/* DESKTOP LINKS */}
                    <Group gap={40} visibleFrom="sm">
                        <Group gap="xl">
                            {NAV_LINKS.map((link) => (
                                <Text
                                    key={link.label}
                                    size="sm" fw={600} c="dimmed"
                                    style={{ cursor: 'pointer', transition: 'color 0.2s' }}
                                    onClick={() => handleNavigation(link.link)}
                                    onMouseEnter={(e) => e.currentTarget.style.color = 'white'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--mantine-color-dimmed)'}
                                >
                                    {link.label}
                                </Text>
                            ))}
                        </Group>

                        <Button
                            variant="light" color="cyan" radius="md" fw={700}
                            onClick={handleLaunch}
                            style={{ boxShadow: '0 0 15px rgba(34, 139, 230, 0.15)' }}
                        >
                            Get Started
                        </Button>
                    </Group>

                    <Burger opened={drawerOpened} onClick={toggleDrawer} hiddenFrom="sm" size="sm" color="white" />
                </Group>
            </Box>

            {/* MOBILE DRAWER */}
            <Drawer
                opened={drawerOpened} onClose={closeDrawer}
                size="100%" padding="md" title="Menu" hiddenFrom="sm" zIndex={1001}
                styles={{ content: { backgroundColor: '#0A0A0A' }, header: { backgroundColor: '#0A0A0A', color: 'white' } }}
            >
                <ScrollArea h={`calc(100vh - 80px)`} mx="-md">
                    <Stack gap="md" p="md">
                        {NAV_LINKS.map((link) => (
                            <Button key={link.label} variant="subtle" color="gray" onClick={() => handleNavigation(link.link)} fullWidth justify="flex-start">
                                {link.label}
                            </Button>
                        ))}
                        <Button variant="gradient" gradient={{ from: 'indigo', to: 'cyan' }} onClick={handleLaunch} fullWidth>
                            Launch Bots
                        </Button>
                    </Stack>
                </ScrollArea>
            </Drawer>
        </Box>
    );
}